// src/hooks/useDefaultLeague.js
// Shared hook for managing the user's default league preference.
// Reads from server (GET /api/account/default-league) ONCE on mount.
// Writes to server (PUT /api/account/default-league) when user changes selection.
import { useState, useEffect, useCallback, useRef } from 'react';
import { accountAPI } from '../services/apiService';

export default function useDefaultLeague(leagues = [], isAuthenticated = false) {
  const [defaultLeagueId, setDefaultLeagueIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  // Fetch from server ONCE when authenticated and leagues are available
  useEffect(() => {
    if (!isAuthenticated || !leagues.length || hasFetched.current) {
      if (!isAuthenticated || !leagues.length) setLoading(false);
      return;
    }

    hasFetched.current = true;
    let cancelled = false;

    const fetchDefault = async () => {
      try {
        const serverValue = await accountAPI.getDefaultLeague();
        if (cancelled) return;

        if (serverValue && leagues.some(l => l.league_id === serverValue)) {
          setDefaultLeagueIdState(serverValue);
        } else {
          // No server preference — default to first league but do NOT auto-save.
          // User must explicitly choose to persist.
          setDefaultLeagueIdState(leagues[0].league_id);
        }
      } catch {
        if (!cancelled && leagues.length) {
          setDefaultLeagueIdState(leagues[0].league_id);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDefault();
    return () => { cancelled = true; };
  }, [isAuthenticated, leagues.length]); // length, not array ref

  const setDefaultLeagueId = useCallback((leagueId) => {
    setDefaultLeagueIdState(leagueId);
    if (isAuthenticated && leagueId) {
      accountAPI.setDefaultLeague(leagueId);
    }
  }, [isAuthenticated]);

  return { defaultLeagueId, setDefaultLeagueId, loading };
}
